'use client'

import { createContext, useContext, useState, useEffect, type ReactNode } from 'react'
import { usePathname } from 'next/navigation'
import type { Dictionary } from '@/dictionaries/types'
import { resolveTenantSlug } from '@/lib/host-routing'
import { type Locale, defaultLocale, isValidLocale } from './config'

const DictContext = createContext<Dictionary | null>(null)
const LocaleContext = createContext<{ locale: Locale; setLocale: (l: Locale) => void }>({
  locale: defaultLocale,
  setLocale: () => {},
})

/** Get the full dictionary for the current locale */
export function useDict(): Dictionary {
  const dict = useContext(DictContext)
  if (!dict) throw new Error('useDict must be used within LocaleProvider')
  return dict
}

/** Get the current locale and a setter to change it */
export function useLocale() {
  return useContext(LocaleContext)
}

/** Simple string interpolation: t("已加入 {count} 項", { count: 5 }) */
export function t(template: string, vars?: Record<string, string | number>): string {
  if (!vars) return template
  return template.replace(/\{(\w+)\}/g, (_, key) => String(vars[key] ?? `{${key}}`))
}

async function loadDictionary(locale: Locale): Promise<Dictionary> {
  switch (locale) {
    case 'zh-Hant': return (await import('@/dictionaries/zh-Hant')).default
    case 'zh-Hans': return (await import('@/dictionaries/zh-Hans')).default
    case 'en': return (await import('@/dictionaries/en')).default
    default: return (await import('@/dictionaries/en')).default
  }
}

function readCookie(name: string): string | undefined {
  if (typeof document === 'undefined') return undefined
  return document.cookie
    .split('; ')
    .find((c) => c.startsWith(`${name}=`))
    ?.split('=')[1]
}

function sniffBrowserLocale(): Locale | undefined {
  const nav = typeof navigator !== 'undefined' ? navigator.language : ''
  if (!nav) return undefined
  // Collapse all Chinese region variants onto our two script-based
  // locales. zh-HK / zh-TW / zh-Hant-* / bare zh → zh-Hant (regional
  // differences are mostly vocabulary, the UI works for all). zh-CN /
  // zh-Hans / zh-SG → zh-Hans.
  if (/^zh-Hans\b/i.test(nav) || /^zh-CN\b/i.test(nav) || /^zh-SG\b/i.test(nav)) return 'zh-Hans'
  if (/^zh\b/i.test(nav)) return 'zh-Hant'
  if (/^en\b/i.test(nav)) return 'en'
  return undefined
}

function getInitialLocale(): Locale {
  if (typeof document === 'undefined') return defaultLocale
  // 1. Explicit user choice wins — cookie was set by Settings → Language
  //    or by the merchant during onboarding. Either way, this user
  //    has already actively picked their preferred language.
  const userChoice = readCookie('locale')
  if (userChoice && isValidLocale(userChoice)) return userChoice

  const tenantDefault = readCookie('tenant_locale')
  const browserSniff = sniffBrowserLocale()

  // 2. On TENANT pages (member portal / public booking / storefront)
  //    we're rendering for a CUSTOMER, not the merchant. Browser sniff
  //    is the customer's own preference, so it wins over the merchant's
  //    `merchants.locale` default — an English-speaking customer
  //    landing on a HK merchant's portal should see English, not 繁中.
  //
  //    Two contexts count as "tenant":
  //
  //      a. Tenant subdomain — `{slug}.novixone.shop` (or
  //         `{slug}.localhost:3000` in dev). The URL bar has no
  //         `/m/` prefix because the proxy rewrites the host-based
  //         path internally, so a path-only check would miss this.
  //
  //      b. Storefront-editor preview iframe — runs on the dashboard
  //         host with a `/m/{slug}/` URL. The merchant is looking at
  //         their tenant page, so customer-style locale resolution
  //         (browser sniff first) is still the right behaviour here.
  //
  //    Outside both: dashboard pages on the merchant host, where the
  //    `tenant_locale` cookie shouldn't normally be present anyway.
  const isTenantContext =
    typeof window !== 'undefined' &&
    (resolveTenantSlug(window.location.host) !== null ||
      window.location.pathname.startsWith('/m/'))
  if (isTenantContext) {
    if (browserSniff) return browserSniff
    if (tenantDefault && isValidLocale(tenantDefault)) return tenantDefault
  } else {
    // Dashboard / non-tenant: tenant_locale cookie shouldn't be set
    // here, but if it is (e.g. shared device after the merchant tested
    // their own portal), still prefer it over browser sniff so the
    // merchant's chosen UI language sticks.
    if (tenantDefault && isValidLocale(tenantDefault)) return tenantDefault
    if (browserSniff) return browserSniff
  }

  // 3. Fallback to platform default
  return defaultLocale
}

/**
 * Public, server-rendered pages that never call `useDict()` (their copy is
 * server-side): render them without waiting for the client dictionary, so
 * the HTML search engines and link previews get is not empty. Keep this
 * list to routes whose client components use `useLocale()` at most.
 */
const DICT_FREE_PREFIXES = ['/developers', '/legal'] as const

function isDictFree(pathname: string | null): boolean {
  return !!pathname && DICT_FREE_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`))
}

export function LocaleProvider({ children, serverLocale }: { children: ReactNode; serverLocale?: Locale }) {
  const pathname = usePathname()
  const dictFree = isDictFree(pathname)
  // Dict-free pages are server-rendered WITH their client components, so the
  // first client render must use the locale the server used (the explicit
  // `locale` cookie, else the default) or React reports a hydration
  // mismatch; the effect below then applies the full client rule (tenant
  // cookie, browser sniff). Other routes render nothing until the
  // dictionary loads, so they can start from the client rule directly.
  const [locale, setLocaleState] = useState<Locale>(() => (dictFree ? serverLocale ?? defaultLocale : getInitialLocale()))
  useEffect(() => {
    if (!dictFree) return
    const client = getInitialLocale()
    if (client !== locale) setLocaleState(client)
    // Once, after hydration.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  const [dict, setDict] = useState<Dictionary | null>(null)

  useEffect(() => {
    loadDictionary(locale).then(setDict)
  }, [locale])

  const setLocale = (l: Locale) => {
    // Cookie write — fast initial render on next page load (no fetch wait).
    document.cookie = `locale=${l}; path=/; max-age=${365 * 24 * 60 * 60}; SameSite=Lax`
    setLocaleState(l)
    // Server persistence — so the merchant gets the same UI language on
    // their phone, on a different browser, on a fresh login. Without this
    // write, merchant_team_members.locale stays at the schema default 'en' even
    // when the user explicitly picks 繁體中文 / 简体中文.
    // Fire-and-forget — cookie already set so UI is already updated.
    if (typeof window !== 'undefined') {
      fetch('/api/merchant/account/locale', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ locale: l }),
      }).catch(() => {
        // Non-fatal: cookie still gives the user the language they picked
        // on this device. Cross-device sync will silently fail until next time.
      })
    }
  }

  if (!dict && !dictFree) return null // Loading dictionary

  return (
    <LocaleContext.Provider value={{ locale, setLocale }}>
      <DictContext.Provider value={dict}>
        {children}
      </DictContext.Provider>
    </LocaleContext.Provider>
  )
}
